import { useMutation, useQueryClient } from '@tanstack/react-query';

import { downloadBlob } from '@xemplo/common-util';
import { payScheduleQueryKeys } from '@xemplo/gp-constants';
import { PayScheduleDetail, RequestParamsV2, ResultV2 } from '@xemplo/gp-types';
import { extractFilenameFromContentDisposition } from '@xemplo/gp-utils';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import {
  PayScheduleAPI,
  PayScheduleAPIV2,
  PayScheduleStatus,
} from './pay-schedule-query.constants';
import {
  GeneratePayrunMutationProps,
  UpdatePayScheduleStatusPayload,
} from './pay-schedule-query.types';

/** Manually generate a payrun for a pay schedule  */
export const useGeneratePayrun = (props: GeneratePayrunMutationProps) => {
  const { payScheduleId, ...mutationOptions } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(
    PayScheduleAPI.generatePayrun(payScheduleId),
    applicationApiUrl,
  );

  return useMutation({
    mutationFn: () => client.post(url).then((resp) => resp.data),
    ...mutationOptions,
  });
};

/** Export pay schedules as an Excel file */
export const useExportPayScheduleMutation = () => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();

  return useMutation({
    mutationFn: async (props: RequestParamsV2) => {
      const url = apiUrlBuilder(PayScheduleAPIV2.exportList, applicationApiUrl, props);
      const response = await client.get<Blob>(url, { responseType: 'blob' });
      const filename = extractFilenameFromContentDisposition(
        response.headers['content-disposition'],
        'pay-schedules-export.xlsx',
      );
      return { blob: response.data, filename };
    },
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename);
    },
  });
};

/** Delete one or more pay schedules */
export const useDeletePaySchedules = () => {
  const queryClient = useQueryClient();
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();

  return useMutation({
    mutationFn: async (payScheduleIds: string[]) => {
      const url = apiUrlBuilder(PayScheduleAPIV2.delete, applicationApiUrl);
      const response = await client.post(url, payScheduleIds);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: payScheduleQueryKeys.lists() });
    },
  });
};

/** Update status (activate/deactivate) of one or more pay schedules */
export const useUpdatePayScheduleStatus = () => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdatePayScheduleStatusPayload) => {
      const url = apiUrlBuilder(PayScheduleAPIV2.updateStatus, applicationApiUrl);
      const response = await client.patch(url, payload);
      return response.data;
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: payScheduleQueryKeys.details() });
      const previousData = queryClient.getQueriesData<ResultV2<PayScheduleDetail>>({
        queryKey: payScheduleQueryKeys.details(),
      });
      queryClient.setQueriesData<ResultV2<PayScheduleDetail>>(
        { queryKey: payScheduleQueryKeys.details() },
        (old) => {
          if (!old?.result || !payload.payscheduleIds.includes(old.result.id)) return old;
          return {
            ...old,
            result: {
              ...old.result,
              isActive: payload.status === PayScheduleStatus.ACTIVATE,
            },
          };
        },
      );
      return { previousData };
    },
    onError: (_err, _payload, context) => {
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: payScheduleQueryKeys.details() });
      queryClient.invalidateQueries({ queryKey: payScheduleQueryKeys.lists() });
    },
  });
};
